import type { ActionArgs, ActionData, AdminAction, SessionMe } from '@contract';
import {
  keepPreviousData,
  QueryClient,
  queryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { AdminApiError, callAdmin } from './api';
import { setActiveMarket } from './market';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, error) =>
        !(
          error instanceof AdminApiError &&
          error.status > 0 &&
          error.status < 500
        ) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

/** Every read is a query keyed by its admin-api action and its args. */
export function adminQuery<A extends AdminAction>(
  action: A,
  args: ActionArgs<A>,
) {
  return queryOptions({
    queryKey: ['admin', action, args] as const,
    queryFn: async () => {
      const data = await callAdmin(action, args);
      // The cell's own currency and time zone, set before anything prints them.
      if (action === 'session.me') {
        setActiveMarket((data as unknown as SessionMe).market);
      }
      return data;
    },
  });
}

/** Invalidates every query of these actions. */
export function invalidate(...actions: AdminAction[]) {
  return Promise.all(
    actions.map((action) =>
      queryClient.invalidateQueries({ queryKey: ['admin', action] }),
    ),
  );
}

/** The cursor pair a keyset list uses: the arg it takes and the field it answers. */
type Cursor =
  | { arg: 'before'; next: 'next_before' }
  | {
      arg: 'before_id';
      next: 'next_before_id';
    };

const BEFORE: Cursor = { arg: 'before', next: 'next_before' };

/**
 * A keyset list read a batch at a time. The table's own Next loads the next
 * batch when the reader reaches the end of what is loaded, so nobody has to
 * look for an Older button. `first` is the first batch's answer, for the
 * summary and counts the server sends with it.
 */
export function useKeysetList<A extends AdminAction, Row>(
  action: A,
  args: ActionArgs<A>,
  options: {
    rows: (page: ActionData<A>) => Row[];
    cursor?: Cursor;
    refetchInterval?: number | false;
  },
) {
  const cursor = options.cursor ?? BEFORE;
  const query = useInfiniteQuery({
    queryKey: ['admin', action, args, 'pages'] as const,
    queryFn: ({ pageParam }) =>
      callAdmin(action, {
        ...args,
        [cursor.arg]: pageParam ?? undefined,
      } as ActionArgs<A>),
    initialPageParam: null as string | number | null,
    getNextPageParam: (page) =>
      ((page as Record<string, unknown>)[cursor.next] as
        | string
        | number
        | null
        | undefined) ?? null,
    refetchInterval: options.refetchInterval,
    placeholderData: keepPreviousData,
  });
  const first = query.data?.pages[0];
  return {
    query,
    first,
    /** The first batch's answer, under the name a plain query uses. */
    data: first,
    error: query.error,
    rows: query.data?.pages.flatMap(options.rows),
    more: {
      key: JSON.stringify([action, args]),
      total: (first as { total?: number } | undefined)?.total,
      hasMore: query.hasNextPage,
      load: async () => {
        const next = await query.fetchNextPage();
        return {
          loaded: next.data?.pages.flatMap(options.rows).length ?? 0,
          hasMore: next.hasNextPage,
        };
      },
    },
  };
}
